const EMOTION_NAMES = new Set([
  'neutral', 'joy', 'laughter', 'excitement', 'agreement', 'gratitude',
  'sadness', 'crying', 'sulk', 'apology', 'struggle', 'anger', 'refusal',
  'surprise', 'fluster', 'shy', 'sympathy',
])
const BRACKET_MARKER_RE = /\[([^\[\]]*)\]\s*/g
const ASCII_MARKER_RE = /^[A-Za-z][A-Za-z0-9_-]*$/
const CONTROL_HINTS = new Set([...EMOTION_NAMES, 'emotion', 'mood', 'feeling'])

/**
 * Model control markers are known emotions, empty brackets, or identifiers
 * that look like lowercase/underscore control syntax. Human-facing labels
 * such as `FAQ` and `OpenAI` are intentionally preserved.
 */
export function isEmotionControlMarker(marker: string): boolean {
  const value = marker.trim()
  if (!value) return true

  const lowerValue = value.toLowerCase()
  if (EMOTION_NAMES.has(lowerValue)) return true

  if (!ASCII_MARKER_RE.test(value)) return false
  return lowerValue.split(/[_-]+/).some((segment) => CONTROL_HINTS.has(segment))
}

function isPotentialEmotionControlFragment(fragment: string): boolean {
  const value = fragment.trim()
  if (!value) return true

  const lowerValue = value.toLowerCase()
  if ([...EMOTION_NAMES].some((emotion) => emotion.startsWith(lowerValue))) return true

  return ASCII_MARKER_RE.test(value)
    && (value === lowerValue || value.includes('_') || value.includes('-'))
}

export function stripEmotionTags(text: string): string {
  return text.replace(
    BRACKET_MARKER_RE,
    (match, marker: string) => isEmotionControlMarker(marker) ? '' : match,
  ).trim()
}

export function stripEmotionTagsForDisplay(text: string): string {
  const cleaned = stripEmotionTags(text)
  const lastOpenBracket = cleaned.lastIndexOf('[')
  const lastCloseBracket = cleaned.lastIndexOf(']')
  if (lastOpenBracket <= lastCloseBracket) return cleaned

  return isPotentialEmotionControlFragment(cleaned.slice(lastOpenBracket + 1))
    ? cleaned.slice(0, lastOpenBracket).trimEnd()
    : cleaned
}

/**
 * 스트리밍 청크를 원문 기준으로 누적한다.
 * 열린 대괄호 뒤의 청크는 태그/일반 괄호 문구가 분리된 경우이므로 공백 없이 잇는다.
 */
export function appendStreamingText(previous: string, next: string): string {
  if (!previous) return next
  if (!next) return previous

  const lastOpenBracket = previous.lastIndexOf('[')
  const lastCloseBracket = previous.lastIndexOf(']')
  const hasUnclosedBracket = lastOpenBracket > lastCloseBracket
  const needsSpace = !hasUnclosedBracket && !/\s$/.test(previous) && !/^\s/.test(next)

  return `${previous}${needsSpace ? ' ' : ''}${next}`
}
